import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { useTenant } from '@/hooks/useTenant'
import type { Feedback } from '@/types/performance'
import {
  AlertCircle,
  AlertTriangle,
  Archive,
  ArrowRight,
  Briefcase,
  Calendar as CalendarIcon,
  CheckCircle2,
  Eye,
  Lightbulb,
  MessageSquare,
  Send,
  Star,
  User,
  Zap
} from 'lucide-react'
import React, { useMemo, useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select'
import { Skeleton } from '../ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '../ui/Tabs'

// 🚀 TanStack Query hook for fetching feedbacks
import { useFeedbacksList } from '@/hooks/queries/useFeedbackQueries'

type FeedbackColumn = 'critical' | 'constructive' | 'recognition' | 'archived'
type ViewType = 'received' | 'sent'

const FeedbacksView = () => {
  const { tenantDomain } = useTenant()

  // 🎯 View type state (received vs sent)
  const [viewType, setViewType] = useState<ViewType>('sent')

  // 🚀 Use TanStack Query for fetching feedbacks with type parameter
  const {
    data: feedbacksResponse,
    isLoading,
    error: queryError,
  } = useFeedbacksList({ limit: 100, type: viewType })

  const feedbacks = feedbacksResponse?.items || []
  const error = queryError ? 'Erro ao carregar feedbacks. Por favor, tente novamente.' : null

  // 🎯 Local UI state only
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null)
  const [isDetailOpen, setIsDetailOpen] = useState(false)
  const [archivedIds, setArchivedIds] = useState<Set<string>>(new Set())
  const [acknowledgedIds, setAcknowledgedIds] = useState<Set<string>>(new Set())
  const [timeRange, setTimeRange] = useState<'30' | '90' | 'all'>('90')

  // Filter by time range
  const filteredFeedbacks = useMemo(() => {
    if (timeRange === 'all') return feedbacks

    const now = new Date()
    const daysAgo = timeRange === '30' ? 30 : 90
    const cutoffDate = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000)

    return feedbacks.filter(f => new Date(f.createdAt) >= cutoffDate)
  }, [feedbacks, timeRange])

  // Group feedbacks by category
  const groupedFeedbacks = useMemo(() => {
    const groups: Record<FeedbackColumn, Feedback[]> = {
      critical: [],
      constructive: [],
      recognition: [],
      archived: []
    }

    filteredFeedbacks.forEach(feedback => {
      if (archivedIds.has(feedback.feedbackId)) {
        groups.archived.push(feedback)
      } else if (feedback.category === 'Crítico') {
        groups.critical.push(feedback)
      } else if (feedback.category === 'Construtivo') {
        groups.constructive.push(feedback)
      } else if (feedback.category === 'Reconhecimento') {
        groups.recognition.push(feedback)
      }
    })

    return groups
  }, [filteredFeedbacks, archivedIds])

  // Stats
  const stats = useMemo(() => {
    const total = filteredFeedbacks.length - archivedIds.size
    const critical = groupedFeedbacks.critical.length
    const constructive = groupedFeedbacks.constructive.length
    const recognition = groupedFeedbacks.recognition.length
    const archived = archivedIds.size

    return { total, critical, constructive, recognition, archived }
  }, [groupedFeedbacks, archivedIds, filteredFeedbacks.length])

  // Handlers
  const handleCardClick = (feedback: Feedback) => {
    setSelectedFeedback(feedback)
    setIsDetailOpen(true)
  }

  const handleArchive = (feedbackId: string) => {
    setArchivedIds(prev => new Set(prev).add(feedbackId))
  }

  const handleAcknowledge = (feedbackId: string) => {
    setAcknowledgedIds(prev => new Set(prev).add(feedbackId))
  }

  const handleAddTo1on1 = (feedbackId: string) => {
    // TODO: Implement add to 1-on-1 agenda
    console.log('Add to 1-on-1:', feedbackId)
  }

  // Helper function to format relative date
  const formatRelativeDate = (dateString: string): string => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInMs = now.getTime() - date.getTime()
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))

    if (diffInDays === 0) return 'Hoje'
    if (diffInDays === 1) return '1 dia atrás'
    if (diffInDays < 7) return `${diffInDays} dias atrás`
    if (diffInDays < 14) return '1 semana atrás'
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} semanas atrás`
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
  }

  // Render loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 w-full bg-brand-dark-lighter" />
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-96 w-full bg-brand-dark-lighter" />
          ))}
        </div>
      </div>
    )
  }

  // Render error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Card className="bg-error/10 border-error/30 max-w-md">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 text-error mx-auto mb-4" />
            <p className="text-error mb-4">{error}</p>
            <Button onClick={() => window.location.reload()} className="btn-primary">
              Tentar Novamente
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header with Tab Switcher */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h2 className="text-white text-2xl font-display font-bold mb-4">
            {viewType === 'received' ? 'Meu Painel de Crescimento' : 'Feedbacks Enviados'}
          </h2>

          {/* Tab Switcher */}
          <Tabs value={viewType} onValueChange={(value) => setViewType(value as ViewType)} className="mb-4">
            <TabsList className="bg-brand-dark-light border border-brand-gray/20">
              {/* <TabsTrigger
                value="received"
                className="data-[state=active]:bg-brand-primary data-[state=active]:text-white text-brand-gray"
              >
                <Inbox className="h-4 w-4 mr-2" />
                Recebidos
              </TabsTrigger> */}
              <TabsTrigger
                value="sent"
                className="data-[state=active]:bg-brand-primary data-[state=active]:text-white text-brand-gray"
              >
                <Send className="h-4 w-4 mr-2" />
                Enviados
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <p className="text-brand-gray font-body">
            Últimos {timeRange === '30' ? '30' : timeRange === '90' ? '90' : 'todos os'} dias • {stats.total} feedbacks {viewType === 'received' ? 'recebidos' : 'enviados'}
          </p>
        </div>

        <div className="flex gap-3">
          <Select value={timeRange} onValueChange={(value: any) => setTimeRange(value)}>
            <SelectTrigger className="btn-secondary w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-brand-dark-light border-brand-gray/20">
              <SelectItem value="30" className="text-white">Últimos 30 dias</SelectItem>
              <SelectItem value="90" className="text-white">Últimos 90 dias</SelectItem>
              <SelectItem value="all" className="text-white">Todos</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-brand-dark-light border-brand-gray/10">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-brand-primary">{stats.total}</p>
            <p className="text-brand-gray text-sm mt-1">Total Ativos</p>
          </CardContent>
        </Card>
        <Card className="bg-brand-dark-light border-brand-gray/10">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-error">{stats.critical}</p>
            <p className="text-brand-gray text-sm mt-1">Críticos</p>
          </CardContent>
        </Card>
        <Card className="bg-brand-dark-light border-brand-gray/10">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-warning">{stats.constructive}</p>
            <p className="text-brand-gray text-sm mt-1">Construtivos</p>
          </CardContent>
        </Card>
        <Card className="bg-brand-dark-light border-brand-gray/10">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-success">{stats.recognition}</p>
            <p className="text-brand-gray text-sm mt-1">Reconhecimentos</p>
          </CardContent>
        </Card>
      </div>

      {/* Empty State */}
      {filteredFeedbacks.length === 0 && (
        <Card className="bg-brand-dark border-brand-gray/10">
          <CardContent className="p-12">
            <div className="text-center max-w-2xl mx-auto">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-brand-blue/10 rounded-monumenta flex items-center justify-center">
                  <MessageSquare className="h-16 w-16 text-brand-blue" />
                </div>
              </div>

              <h3 className="text-white text-2xl font-display mb-3">
                {viewType === 'received' ? 'Nenhum feedback recebido ainda' : 'Nenhum feedback enviado ainda'}
              </h3>

              <p className="text-brand-gray text-base mb-8 leading-relaxed">
                {viewType === 'received'
                  ? 'Feedbacks de colegas e gestores aparecerão aqui. Eles são essenciais para seu desenvolvimento profissional.'
                  : 'Feedbacks que você enviar para colegas aparecerão aqui. Contribua para o desenvolvimento da equipe!'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Kanban Board */}
      {filteredFeedbacks.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Critical Column */}
          <FeedbackColumn
            title="Críticos"
            description="Requerem ação imediata"
            count={groupedFeedbacks.critical.length}
            color="error"
            feedbacks={groupedFeedbacks.critical}
            onCardClick={handleCardClick}
            onArchive={handleArchive}
            onAcknowledge={handleAcknowledge}
            onAddTo1on1={handleAddTo1on1}
            acknowledgedIds={acknowledgedIds}
            formatRelativeDate={formatRelativeDate}
            viewType={viewType}
          />

          {/* Constructive Column */}
          <FeedbackColumn
            title="Desenvolvimento"
            description="Áreas de melhoria"
            count={groupedFeedbacks.constructive.length}
            color="warning"
            feedbacks={groupedFeedbacks.constructive}
            onCardClick={handleCardClick}
            onArchive={handleArchive}
            onAcknowledge={handleAcknowledge}
            onAddTo1on1={handleAddTo1on1}
            acknowledgedIds={acknowledgedIds}
            formatRelativeDate={formatRelativeDate}
            viewType={viewType}
          />

          {/* Recognition Column */}
          <FeedbackColumn
            title="Pontos Fortes"
            description="Continue assim!"
            count={groupedFeedbacks.recognition.length}
            color="success"
            feedbacks={groupedFeedbacks.recognition}
            onCardClick={handleCardClick}
            onArchive={handleArchive}
            onAcknowledge={handleAcknowledge}
            onAddTo1on1={handleAddTo1on1}
            acknowledgedIds={acknowledgedIds}
            formatRelativeDate={formatRelativeDate}
            viewType={viewType}
          />

          {/* Archived Column */}
          <FeedbackColumn
            title="Arquivados"
            description={`${stats.archived} itens`}
            count={groupedFeedbacks.archived.length}
            color="gray"
            feedbacks={groupedFeedbacks.archived}
            onCardClick={handleCardClick}
            onArchive={handleArchive}
            onAcknowledge={handleAcknowledge}
            onAddTo1on1={handleAddTo1on1}
            acknowledgedIds={acknowledgedIds}
            formatRelativeDate={formatRelativeDate}
            viewType={viewType}
            isArchived
          />
        </div>
      )}

      {/* Feedback Detail Modal */}
      <FeedbackDetailModal
        feedback={selectedFeedback}
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        onArchive={handleArchive}
        onAcknowledge={handleAcknowledge}
        onAddTo1on1={handleAddTo1on1}
        isAcknowledged={selectedFeedback ? acknowledgedIds.has(selectedFeedback.feedbackId) : false}
        isArchived={selectedFeedback ? archivedIds.has(selectedFeedback.feedbackId) : false}
        viewType={viewType}
      />
    </div>
  )
}

// Feedback Column Component
interface FeedbackColumnProps {
  title: string
  description: string
  count: number
  color: 'error' | 'warning' | 'success' | 'gray'
  feedbacks: Feedback[]
  onCardClick: (feedback: Feedback) => void
  onArchive: (feedbackId: string) => void
  onAcknowledge: (feedbackId: string) => void
  onAddTo1on1: (feedbackId: string) => void
  acknowledgedIds: Set<string>
  formatRelativeDate: (date: string) => string
  viewType: ViewType
  isArchived?: boolean
}

const FeedbackColumn: React.FC<FeedbackColumnProps> = ({
  title,
  description,
  count,
  color,
  feedbacks,
  onCardClick,
  onArchive,
  onAcknowledge,
  onAddTo1on1,
  acknowledgedIds,
  formatRelativeDate,
  viewType,
  isArchived = false
}) => {
  const colorClasses = {
    error: 'border-error/30 bg-error/5',
    warning: 'border-warning/30 bg-warning/5',
    success: 'border-success/30 bg-success/5',
    gray: 'border-brand-gray/20 bg-brand-dark-lighter'
  }

  return (
    <div className="space-y-3">
      {/* Column Header */}
      <Card className={`${colorClasses[color]} border`}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-white text-lg font-display">{title}</CardTitle>
              <p className="text-brand-gray text-sm mt-1">{description}</p>
            </div>
            <Badge variant="outline" className="text-white border-brand-gray/30">
              {count}
            </Badge>
          </div>
        </CardHeader>
      </Card>

      {/* Feedback Cards */}
      <div className="space-y-3 min-h-32">
        {feedbacks.length === 0 ? (
          <Card className="bg-brand-dark-light border-brand-gray/10">
            <CardContent className="p-6 text-center">
              <p className="text-brand-gray text-sm">
                {isArchived ? 'Nenhum item arquivado' : 'Nenhum feedback nesta categoria'}
              </p>
            </CardContent>
          </Card>
        ) : (
          feedbacks.map(feedback => (
            <FeedbackCard
              key={feedback.feedbackId}
              feedback={feedback}
              onClick={() => onCardClick(feedback)}
              onArchive={() => onArchive(feedback.feedbackId)}
              onAcknowledge={() => onAcknowledge(feedback.feedbackId)}
              onAddTo1on1={() => onAddTo1on1(feedback.feedbackId)}
              isAcknowledged={acknowledgedIds.has(feedback.feedbackId)}
              formatRelativeDate={formatRelativeDate}
              color={color}
              viewType={viewType}
            />
          ))
        )}
      </div>
    </div>
  )
}

// Feedback Card Component
interface FeedbackCardProps {
  feedback: Feedback
  onClick: () => void
  onArchive: () => void
  onAcknowledge: () => void
  onAddTo1on1: () => void
  isAcknowledged: boolean
  formatRelativeDate: (date: string) => string
  color: 'error' | 'warning' | 'success' | 'gray'
  viewType: ViewType
}

const FeedbackCard: React.FC<FeedbackCardProps> = ({
  feedback,
  onClick,
  onArchive,
  onAcknowledge,
  onAddTo1on1,
  isAcknowledged,
  formatRelativeDate,
  color,
  viewType
}) => {
  const colorClasses = {
    error: 'border-error/20 hover:border-error/40 hover:bg-error/5',
    warning: 'border-warning/20 hover:border-warning/40 hover:bg-warning/5',
    success: 'border-success/20 hover:border-success/40 hover:bg-success/5',
    gray: 'border-brand-gray/20 hover:border-brand-gray/40 hover:bg-brand-dark-lighter'
  }

  // Show the appropriate name based on view type
  const displayName = viewType === 'received' ? feedback.fromEmployeeName : feedback.toEmployeeName

  return (
    <Card
      className={`${colorClasses[color]} border bg-brand-dark-light cursor-pointer transition-all group`}
      onClick={onClick}
    >
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <p className="text-white text-sm font-medium">{formatRelativeDate(feedback.createdAt)}</p>
            <div className="flex items-center gap-2 mt-1">
              <User className="h-3 w-3 text-brand-gray" />
              <p className="text-brand-gray text-xs">
                {viewType === 'received' ? 'De: ' : 'Para: '}{displayName}
              </p>
            </div>
          </div>
          {isAcknowledged && (
            <CheckCircle2 className="h-4 w-4 text-success flex-shrink-0" />
          )}
        </div>

        {/* Project */}
        {feedback.project && (
          <div className="flex items-center gap-2 mb-3">
            <Briefcase className="h-3 w-3 text-brand-blue" />
            <p className="text-brand-blue text-xs truncate">{feedback.project}</p>
          </div>
        )}

        {/* Preview Text */}
        <div className="space-y-2 mb-3">
          <div className="flex items-start gap-2">
            <MessageSquare className="h-3 w-3 text-brand-gray mt-0.5 flex-shrink-0" />
            <p className="text-white text-sm line-clamp-2">{feedback.observation}</p>
          </div>
        </div>

        {/* Impact Preview */}
        {feedback.impact && (
          <div className="flex items-start gap-2 mb-3">
            <Zap className="h-3 w-3 text-warning mt-0.5 flex-shrink-0" />
            <p className="text-brand-gray text-xs line-clamp-2">{feedback.impact}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-3 border-t border-brand-gray/10">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              onClick()
            }}
            className="text-brand-blue hover:text-brand-blue/80 hover:bg-brand-blue/10 text-xs h-7 px-2"
          >
            Ver completo
            <ArrowRight className="h-3 w-3 ml-1" />
          </Button>

          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                onAddTo1on1()
              }}
              className="text-brand-gray hover:text-white hover:bg-brand-dark text-xs h-7 w-7 p-0"
              title="Adicionar ao 1-on-1"
            >
              <CalendarIcon className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                onArchive()
              }}
              className="text-brand-gray hover:text-white hover:bg-brand-dark text-xs h-7 w-7 p-0"
              title="Arquivar"
            >
              <Archive className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// Feedback Detail Modal Component
interface FeedbackDetailModalProps {
  feedback: Feedback | null
  isOpen: boolean
  onClose: () => void
  onArchive: (feedbackId: string) => void
  onAcknowledge: (feedbackId: string) => void
  onAddTo1on1: (feedbackId: string) => void
  isAcknowledged: boolean
  isArchived: boolean
  viewType: ViewType
}

const FeedbackDetailModal: React.FC<FeedbackDetailModalProps> = ({
  feedback,
  isOpen,
  onClose,
  onArchive,
  onAcknowledge,
  onAddTo1on1,
  isAcknowledged,
  isArchived,
  viewType
}) => {
  if (!feedback) return null

  const categoryConfig = {
    'Crítico': { color: 'error', icon: AlertTriangle, bg: 'bg-error/10', border: 'border-error/30', text: 'text-error' },
    'Construtivo': { color: 'warning', icon: Lightbulb, bg: 'bg-warning/10', border: 'border-warning/30', text: 'text-warning' },
    'Reconhecimento': { color: 'success', icon: Star, bg: 'bg-success/10', border: 'border-success/30', text: 'text-success' }
  }

  const config = categoryConfig[feedback.category]
  const Icon = config.icon

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-brand-dark-light border-brand-gray/20 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl text-white flex items-center gap-3">
            <Icon className={`h-6 w-6 ${config.text}`} />
            Feedback {feedback.category}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Meta Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className={`p-4 rounded-monumenta ${config.bg} border ${config.border}`}>
              <p className="text-brand-gray text-sm mb-1">{viewType === 'received' ? 'De:' : 'Para:'}</p>
              <p className="text-white font-medium">
                {viewType === 'received' ? feedback.fromEmployeeName : feedback.toEmployeeName}
              </p>
            </div>
            <div className={`p-4 rounded-monumenta ${config.bg} border ${config.border}`}>
              <p className="text-brand-gray text-sm mb-1">Data:</p>
              <p className="text-white font-medium">
                {new Date(feedback.createdAt).toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric'
                })}
              </p>
            </div>
          </div>

          {/* Project */}
          {feedback.project && (
            <div className="p-4 rounded-monumenta bg-brand-dark border border-brand-gray/20">
              <p className="text-brand-gray text-sm mb-1">Projeto/Cliente:</p>
              <p className="text-white">{feedback.project}</p>
            </div>
          )}

          {/* COIN/SBI Sections */}
          <div className="space-y-4">
            {/* Context */}
            {feedback.context && (
              <div className="p-4 rounded-monumenta bg-brand-dark border border-brand-gray/20">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-brand-blue" />
                  <p className="text-brand-blue text-sm font-medium">CONTEXTO</p>
                </div>
                <p className="text-white leading-relaxed">{feedback.context}</p>
              </div>
            )}

            {/* Observation */}
            <div className="p-4 rounded-monumenta bg-brand-dark border border-brand-gray/20">
              <div className="flex items-center gap-2 mb-2">
                <Eye className="w-4 h-4 text-brand-primary" />
                <p className="text-brand-primary text-sm font-medium">OBSERVAÇÃO</p>
              </div>
              <p className="text-white leading-relaxed">{feedback.observation}</p>
            </div>

            {/* Impact */}
            <div className="p-4 rounded-monumenta bg-brand-dark border border-brand-gray/20">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-warning" />
                <p className="text-warning text-sm font-medium">IMPACTO</p>
              </div>
              <p className="text-white leading-relaxed">{feedback.impact}</p>
            </div>

            {/* Next Steps */}
            <div className="p-4 rounded-monumenta bg-brand-dark border border-brand-gray/20">
              <div className="flex items-center gap-2 mb-2">
                <ArrowRight className="w-4 h-4 text-success" />
                <p className="text-success text-sm font-medium">PRÓXIMOS PASSOS</p>
              </div>
              <p className="text-white leading-relaxed">{feedback.nextSteps}</p>
            </div>
          </div>

          {/* Status */}
          {isAcknowledged && (
            <div className="flex items-center gap-2 p-3 rounded-monumenta bg-success/10 border border-success/30">
              <CheckCircle2 className="h-4 w-4 text-success" />
              <p className="text-success text-sm">Feedback reconhecido</p>
            </div>
          )}

          {isArchived && (
            <div className="flex items-center gap-2 p-3 rounded-monumenta bg-brand-gray/10 border border-brand-gray/30">
              <Archive className="h-4 w-4 text-brand-gray" />
              <p className="text-brand-gray text-sm">Feedback arquivado</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-between gap-3 pt-4 border-t border-brand-gray/20">
          <Button
            variant="outline"
            onClick={onClose}
            className="btn-secondary"
          >
            Fechar
          </Button>
          <div className="flex gap-3">
            {!isAcknowledged && viewType === 'received' && (
              <Button
                onClick={() => {
                  onAcknowledge(feedback.feedbackId)
                  onClose()
                }}
                className="btn-secondary"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Reconhecer
              </Button>
            )}
            {viewType === 'received' && (
              <Button
                onClick={() => {
                  onAddTo1on1(feedback.feedbackId)
                  onClose()
                }}
                className="btn-primary"
              >
                <CalendarIcon className="h-4 w-4 mr-2" />
                Adicionar ao 1-on-1
              </Button>
            )}
            {!isArchived && (
              <Button
                onClick={() => {
                  onArchive(feedback.feedbackId)
                  onClose()
                }}
                variant="outline"
                className="border-brand-gray/30 text-brand-gray hover:bg-brand-dark"
              >
                <Archive className="h-4 w-4 mr-2" />
                Arquivar
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default FeedbacksView
